import React, { FunctionComponent } from "react";
import styles from "./Header.module.scss";
import HeaderProps from "./Header.interface";
import { CLOSE_ALL_OVERLAYS, OPEN_MEGA_DROPDOWN_MENU, OPEN_MOBILE_MENU } from "layout/redux/overlay.actions";
import { useDispatch, useSelector } from "react-redux";
import Anchor from "components/Anchor/Anchor";
import Logo from "./components/Logo/Logo";
import { AnimatePresence, motion } from "framer-motion";
import BreadcrumbLink from "./components/BreadcrumbLink/BreadcrumbLink";
import combineClasses from "helpers/combine-classes";
import useIsAppBarVisible from "./hooks/useIsAppBarVisible";
import IconViewer from "components/IconViewer/IconViewer";
import useWindowDimensions from "hooks/useWindowDimensions";

const Header: FunctionComponent<HeaderProps> = (props) => {
    const dispatch = useDispatch();
    const menuOpen = useSelector(state => state.overlay.menuOpen);
    const page = useSelector(state => state.page.content);
    const { width } = useWindowDimensions();
    const whiteBreadcrumbs = page?.breadcrumbsDarkBackground ? styles.white_breadcrumbs : undefined;

    const result = useIsAppBarVisible();
    const appBarOverThreshold = result.appBarOverThreshold;

    const openMobileMenu = () => {
        // TODO: Based on useWindowDimensions, switch between MobileMenu and MegaDropdown
        if (!menuOpen) {
            dispatch({
                type: OPEN_MOBILE_MENU,
                menu: undefined,
                subMenu: undefined,
                menuOpen: true
            });
        }
        else {
            dispatch({
                type: CLOSE_ALL_OVERLAYS,
            });
        }
    };

    const handleMenuClick = () => {
        if (!menuOpen) {
            dispatch({
                type: OPEN_MEGA_DROPDOWN_MENU,
                menu: props.settings?.menu,
                subMenu: undefined,
                menuOpen: true
            });
        }
        else {
            dispatch({
                type: CLOSE_ALL_OVERLAYS,
            });
        }
    };

    const handleClickCloseMenu = () => {
        dispatch({
            type: CLOSE_ALL_OVERLAYS,
        });
    };

    const transition = {
        duration: 0.3,
        ease: "easeInOut"
    };

    const color = menuOpen ? "var(--color-navy-blue)" : "var(--color-just-orange)";

    return (
        <header className={styles.wrapper}>
            <header className={styles.header}>
                <div className={styles.inner}>
                    <div className={styles.logo}>
                        {/* Logo */}
                        <div onClick={handleClickCloseMenu}>
                            <Anchor aria="Work Live Stay logo | Homepage" to="/" className={styles.logo}>
                                <Logo appBarOverThreshold={appBarOverThreshold} />
                            </Anchor>
                        </div>
                    </div>

                    <div className={styles.menu_right}>
                        {/* Search Button */}
                        <div onClick={handleClickCloseMenu}>
                            <Anchor to="/search-page" aria="Search" className={styles.search_button}>
                                <IconViewer name="search" className={styles.search_icon} />
                            </Anchor>
                        </div>

                        {/* Menu Toggle */}
                        <div className={styles.menu_wrapper} onClick={() => handleMenuClick()}>
                            <button aria-label="Hamburger Main Menu" className={styles.hamburger_menu_button}>
                                <motion.span animate={{ y: menuOpen ? 8 : 0, rotate: menuOpen ? 45 : 0, backgroundColor: color }} transition={transition} />
                                <motion.span animate={{ rotate: menuOpen ? -45 : 0, backgroundColor: color }} transition={transition} />
                                <motion.span animate={{ y: menuOpen ? -8 : 0, opacity: menuOpen ? 0 : 1 }} transition={transition} />
                            </button>
                            <motion.div className={styles.hamburger_menu_background} style={{ translateX: "-50%", translateY: "-50%" }} animate={{ scale: menuOpen ? 100 : 1 }} transition={{ duration: 1, ease: "easeInOut" }} />
                        </div>

                        {/* Mobile Menu Toggle */}
                        <div className={styles.mobile_menu_wrapper}>
                            <button aria-label="Hamburger Mobile Main Menu" className={styles.hamburger_menu_button} onClick={() => openMobileMenu()}>
                                <motion.span animate={{ y: menuOpen ? 8 : 0, rotate: menuOpen ? 45 : 0, backgroundColor: color }} transition={transition} />
                                <motion.span animate={{ rotate: menuOpen ? -45 : 0, backgroundColor: color }} transition={transition} />
                                <motion.span animate={{ y: menuOpen ? -8 : 0, opacity: menuOpen ? 0 : 1 }} transition={transition} />
                            </button>
                            <motion.div className={styles.hamburger_menu_background} style={{ translateX: "-50%", translateY: "-50%" }} animate={{ scale: menuOpen ? 55 : 1 }} transition={{ duration: 0.7 }} />
                        </div>
                    </div>
                </div>
            </header>
            {/* Breadcrumbs */}
            <AnimatePresence>
                {page?._breadcrumbs && page._breadcrumbs.length > 1 && !menuOpen ? (
                    <motion.div style={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={combineClasses([styles.breadcrumb_wrapper, whiteBreadcrumbs])}>
                        {page._breadcrumbs.map((link, index) => (
                            <BreadcrumbLink key={link._name} isLast={page._breadcrumbs?.length === index + 1} {...link} />
                        ))}
                    </motion.div>
                ) : null}
            </AnimatePresence>

            {width >= 1024 ? (
                <div className={styles.header_circle}>
                    <div className={styles.hamburger_menu_background_outer} />
                </div>
            ) : null}
        </header>
    );
};

export default Header;